// ----------------------------------------------------------------------
// Lead — contract of POST /api/v1/leads (Api\LeadsController@store).
// Every public form that captures a lead should build this payload so the
// accepted fields and the `origin` enum live in exactly one place.
// ----------------------------------------------------------------------

/**
 * `leads.origin` is an **enum column** — anything outside this list is rejected
 * with a 422 and the lead is lost. Describes *which kind of form* produced it.
 */
export const LEAD_ORIGINS = [
  "form",
  "quiz",
  "contact",
  "formation",
  "chat",
  "other",
] as const;

export type LeadOrigin = (typeof LEAD_ORIGINS)[number];

/**
 * `leads.source` is a free-text acquisition/provenance label. The admin filter
 * is built from the distinct values already in the table, so a new value simply
 * shows up in the dropdown — no migration needed. Values in use so far:
 * "Site web" (API default), "Quiz d'orientation", "Session", plus the
 * marketing channels from the seeders (Google Ads, SEO, Meta Ads, …).
 */
export const LEAD_SOURCES = {
  /** Enrollment request sent from a session row on the formation detail page. */
  session: "Session",
} as const;

export interface ILeadPayload {
  first_name: string;
  last_name?: string;
  email: string;
  phone?: string;
  /** → `leads.company`, shown as "Société" on the admin lead page. */
  company?: string;
  /** → `leads.city`, shown next to the zone on the admin lead page. */
  city?: string;
  /** Logged as the lead's first `LeadActivity` ("created") description. */
  message: string;
  /** → `leads.formation_title`, drives the admin "Formation" filter. */
  formation_title?: string;
  /** → `leads.formation_theme` (the category), drives the admin "Thème" filter. */
  formation_theme?: string;
  origin: LeadOrigin;
  source?: string;
  wants_appointment?: boolean;
  appointment_date?: string;
}
